import { uniqBy } from "lodash";
import pluralize from "pluralize";
import * as yup from "yup";

import { getDeviceScopedKeys } from "@ds/modules/settings/projects/utils/formation-roles";

import {
  getFormationPropertiesValidationSchema,
  type FormationPropertyValidationData,
  type Shape,
} from "@ds/utils/validation";

import { getPlayerRoleIssues } from "./player-roles";

const nameSchema = yup.string().required("Please specify experience name");

const locationIdErrorMessage = "Please choose location from list";
const locationIdSchema = yup
  .number()
  .positive(locationIdErrorMessage)
  .typeError(locationIdErrorMessage)
  .required(locationIdErrorMessage);

const playersSchema = yup
  .array()
  .of(yup.object().shape<Shape<Player>>({}))
  .test("isUniquePlayers", (players, ctx) => {
    const assignedPlayersNames = (players as Player[])
      ?.filter(
        ({ experience_id }) => experience_id && experience_id !== (ctx.parent as ExperienceFormData).experience.id,
      )
      .map(({ name }) => name);

    if (uniqBy(players, ({ id }) => id).length !== (players?.length || 0)) {
      return ctx.createError({
        message: "Remove duplicates from the list.",
      });
    }

    if (assignedPlayersNames?.length) {
      const playersStatement = pluralize("Player", assignedPlayersNames.length);
      return ctx.createError({
        message: `${playersStatement}: "${assignedPlayersNames.join(
          ", ",
        )}", already assigned to another experience, remove from the list first.`,
      });
    }

    return true;
  });

const getFormationSchema = (validationData: FormationPropertyValidationData[]) =>
  yup.object().shape<Shape<Experience>>({
    formation: getFormationPropertiesValidationSchema(validationData),
  });

export const createValidationSchema = {
  step1: yup.object().shape<Shape<ExperienceFormData>>({
    experience: yup.object().shape<Shape<Experience>>({
      location_id: locationIdSchema,
    }),
    players: playersSchema,
  }),
  getStep2: getFormationSchema,
  step3: yup.object().shape<Shape<Experience>>({
    name: nameSchema,
  }),
};

export const updateValidationSchema = {
  info: (validationData: FormationPropertyValidationData[]) =>
    getFormationSchema(validationData).concat(
      yup.object().shape<Shape<Experience>>({
        name: nameSchema,
        location_id: locationIdSchema,
      }),
    ),
  players: yup.object().shape<Shape<ExperienceFormData>>({
    players: playersSchema,
  }),
  formation: getFormationSchema,
};

/** Form path of a player's role, or of its value of one of the role's fields */
export const getPlayerRoleFieldPath = (playerId: number | string, field: string) =>
  field === "role"
    ? `experience.formation.devices.${playerId}.role`
    : `experience.formation.devices.${playerId}.properties.${field}.value`;

/**
 * Checks a player's values of its role's fields against the fields' rules, leaving out the required rule since a
 * missing value is only a warning. The errors' paths are relative to the player's device formation.
 */
export const createDeviceValuesValidator = (roles: FormationRoleMap, properties: TemplateFormationFieldPropertyMap) => {
  const deviceSchema = getFormationPropertiesValidationSchema(
    getDeviceScopedKeys(roles)
      .filter(field => !!properties[field])
      .map(field => ({
        field,
        validationRules: properties[field].metadata?.validation_rules || [],
        dropdownOptions:
          (properties[field].metadata as { options?: Array<{ value: string; label: string }> })?.options || [],
      })),
    true,
  );

  return (deviceFormation?: ExperienceDeviceFormation | null) => {
    try {
      deviceSchema.validateSync(deviceFormation || {}, { abortEarly: false });
      return [];
    } catch (error) {
      return yup.ValidationError.isError(error) ? error.inner.map(({ path, message }) => ({ path, message })) : [];
    }
  };
};

/**
 * The players card when the project's formation assigns roles: players as before, each player's values checked
 * against its field's rules, and the errors the server would reject the save for (a role over its `max`, a
 * duplicate `unique` value). A missing role or required value is left as a warning, so it never blocks saving.
 */
export const getPlayerRolesValidationSchema = (
  roles: FormationRoleMap,
  properties: TemplateFormationFieldPropertyMap,
) => {
  const validateDeviceValues = createDeviceValuesValidator(roles, properties);

  // Only the players still in the list are checked: the form keeps the values of a player removed in the same
  // edit, and an error on it would block the save without a row to show it on.
  const getValueErrors = (playerId: number, deviceFormation?: ExperienceDeviceFormation) =>
    validateDeviceValues(deviceFormation).map(({ path, message }) => ({
      path: `experience.formation.devices.${playerId}.${path}`,
      message,
    }));

  return updateValidationSchema.players.test("playerRoles", function validatePlayerRoles(value) {
    const formData = value as unknown as ExperienceFormData;
    const devices = formData?.experience?.formation?.devices;
    const playerIds = (formData?.players || []).map(({ id }) => id);
    const issues = getPlayerRoleIssues({ roles, properties, devices, playerIds });

    const errors = [
      ...playerIds.flatMap(playerId => getValueErrors(playerId, devices?.[playerId])),
      ...Object.entries(issues).flatMap(([playerId, playerIssues]) =>
        playerIssues
          .filter(({ severity }) => severity === "error")
          .map(({ field, message }) => ({ path: getPlayerRoleFieldPath(playerId, field), message })),
      ),
    ].map(error => this.createError(error));

    return errors.length ? new yup.ValidationError(errors) : true;
  });
};
